import { filesFromClipboard } from '../ai-elements/prompt-input';
import { EditorContent, Node as TiptapNode, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import {
  encodeAgentMentionLabel,
  type InlineReference,
  parseInlineReferenceValue,
  type TiptapContent,
} from '../../lib/inline-reference';

export type { InlineReference } from '../../lib/inline-reference';

export type InlineReferenceEditorHandle = {
  focus(): void;
  insertReference(reference: InlineReference, queryLength: number): void;
  insertReferenceAtSelection(reference: InlineReference): void;
  replaceReference(position: number, reference: InlineReference): void;
  setSelectionRange(start: number, end: number): void;
};

export type InlineReferenceEditorProps = {
  disabled?: boolean;
  onChange(value: string): void;
  onKeyDown(event: KeyboardEvent): void;
  onFilesPaste?(files: File[]): void;
  onReferenceClick?(reference: InlineReference, position: number): void;
  placeholder: string;
  references?: readonly InlineReference[];
  /** Shown instead of the placeholder while the editor is empty; Tab, → or Enter types it in. */
  suggestion?: string;
  value: string;
  variant?: 'default' | 'template' | 'template-field';
};

function referenceToken(reference: InlineReference): string {
  if (/^\{\{[^{}]+\}\}$/.test(reference.id)) return reference.id;
  if (reference.kind === 'skill') return `[$${reference.label}](${reference.id})`;
  return `@[${encodeAgentMentionLabel(reference.label)}](${reference.kind}:${reference.id})`;
}

/** ProseMirror builds untagged elements in the HTML namespace, where an `svg` draws nothing. */
const SVG_TAG = 'http://www.w3.org/2000/svg svg';

function referenceIcon(reference: InlineReference) {
  if (/^\{\{[^{}]+\}\}$/.test(reference.id)) {
    return [
      SVG_TAG,
      { 'aria-hidden': 'true', viewBox: '0 0 24 24' },
      [
        'path',
        {
          d: 'M8 3H5a2 2 0 0 0-2 2v3m13-5h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3m13 5h3a2 2 0 0 0 2-2v-3M8 8l-3 4 3 4m8-8 3 4-3 4',
          fill: 'none',
          stroke: 'currentColor',
        },
      ],
    ];
  }
  if (reference.kind === 'skill') {
    return [
      SVG_TAG,
      { 'aria-hidden': 'true', viewBox: '0 0 24 24' },
      [
        'path',
        {
          d: 'm12 2 8 4.5v9L12 20l-8-4.5v-9L12 2Zm0 0v9m8-4.5-8 4.5m-8-4.5 8 4.5m0 9v-9',
          fill: 'none',
          stroke: 'currentColor',
        },
      ],
    ];
  }
  if (reference.kind === 'artifact') {
    return [
      SVG_TAG,
      { 'aria-hidden': 'true', viewBox: '0 0 24 24' },
      [
        'path',
        {
          d: 'M4 4h6l2 2h8v14H4V4Zm0 6h16',
          fill: 'none',
          stroke: 'currentColor',
        },
      ],
    ];
  }
  return [
    SVG_TAG,
    { 'aria-hidden': 'true', viewBox: '0 0 24 24' },
    [
      'path',
      {
        d: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Zm0 0c2.5-2.7 4-6.1 4-10S14.5 4.7 12 2m0 20c-2.5-2.7-4-6.1-4-10s1.5-7.3 4-10M2 12h20',
        fill: 'none',
        stroke: 'currentColor',
      },
    ],
  ];
}

const ReferenceNode = TiptapNode.create({
  name: 'reference',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      id: { default: '' },
      kind: { default: 'reference' },
      label: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-reference-id]' }];
  },

  renderHTML({ node }) {
    const reference = node.attrs as InlineReference;
    return [
      'span',
      {
        'data-reference-id': reference.id,
        'data-reference-kind': reference.kind,
        'data-reference-label': reference.label,
        class: `crowdex-reference${/^\{\{[^{}]+\}\}$/.test(reference.id) ? ' crowdex-reference-template' : ''}`,
        title: `${reference.kind} reference`,
      },
      [
        'span',
        { class: 'crowdex-reference-leading' },
        ['span', { class: 'crowdex-reference-icon' }, referenceIcon(reference)],
        ['span', { class: 'crowdex-reference-remove', 'aria-hidden': 'true' }, '×'],
      ],
      ['span', {}, reference.label],
    ];
  },
});

function serializeContent(node: TiptapContent): string {
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return '\n';
  if (node.type === 'reference' && node.attrs !== undefined) return referenceToken(node.attrs);
  return node.content?.map(serializeContent).join('') ?? '';
}

export const InlineReferenceEditor = forwardRef<
  InlineReferenceEditorHandle,
  InlineReferenceEditorProps
>(function InlineReferenceEditor(
  {
    disabled = false,
    onChange,
    onKeyDown,
    onFilesPaste,
    onReferenceClick,
    placeholder,
    references = [],
    suggestion,
    value,
    variant = 'default',
  },
  ref,
) {
  const showSuggestion = suggestion !== undefined && value.length === 0;
  const suggestionRef = useRef<string | undefined>(undefined);
  suggestionRef.current = showSuggestion ? suggestion : undefined;
  const lastEmittedValueRef = useRef(value);
  const referencesKey = references
    .map((reference) => `${reference.kind}:${reference.id}:${reference.label}`)
    .join('|');
  const lastReferencesKeyRef = useRef(referencesKey);
  /** The `value` the last sync saw: only a new one is an external change. */
  const lastValuePropRef = useRef(value);
  /** What the form submits: always the editor's text, which `value` may trail behind. */
  const submittedRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const onKeyDownRef = useRef(onKeyDown);
  const onFilesPasteRef = useRef(onFilesPaste);
  const onReferenceClickRef = useRef(onReferenceClick);
  onChangeRef.current = onChange;
  onKeyDownRef.current = onKeyDown;
  onFilesPasteRef.current = onFilesPaste;
  onReferenceClickRef.current = onReferenceClick;

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        code: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        orderedList: false,
      }),
      ReferenceNode,
    ],
    content: parseInlineReferenceValue(value, references, variant !== 'default'),
    editable: !disabled,
    editorProps: {
      attributes: {
        // A text field to assistive tech, not a bare editable region.
        role: 'textbox',
        'aria-multiline': variant === 'template-field' ? 'false' : 'true',
        'aria-label': placeholder,
        class:
          variant === 'template'
            ? 'min-h-40 w-full whitespace-pre-wrap break-words pr-9 text-left text-sm leading-6 outline-none'
            : variant === 'template-field'
              ? 'h-9 w-full overflow-hidden whitespace-nowrap py-1.5 pr-9 pl-2.5 text-left text-xs leading-6 outline-none [&_p]:overflow-hidden [&_p]:text-ellipsis [&_p]:whitespace-nowrap'
              : 'min-h-14 w-full whitespace-pre-wrap break-words px-4 pt-3.5 pb-2 text-left text-sm leading-6 outline-none',
      },
      handleDOMEvents: {
        click(view, event) {
          const target = event.target as HTMLElement;
          const referenceElement = target.closest<HTMLElement>('[data-reference-id]');
          if (referenceElement === null) return false;
          const nodePosition = view.posAtDOM(referenceElement, 0);
          const node = view.state.doc.nodeAt(nodePosition);
          if (node?.type.name !== 'reference') return false;
          if (target.closest('.crowdex-reference-remove') !== null) {
            view.dispatch(view.state.tr.delete(nodePosition, nodePosition + node.nodeSize));
            view.focus();
            return true;
          }
          onReferenceClickRef.current?.(node.attrs as InlineReference, nodePosition);
          return true;
        },
      },
      handlePaste(_view, event) {
        const files = filesFromClipboard(event.clipboardData?.items);
        const publish = onFilesPasteRef.current;
        if (files.length === 0 || publish === undefined) return false;
        event.preventDefault();
        publish(files);
        return true;
      },
      handleKeyDown(view, event) {
        const pending = suggestionRef.current;
        if (
          pending !== undefined &&
          view.state.doc.textContent.length === 0 &&
          (event.key === 'Tab' || event.key === 'ArrowRight' || event.key === 'Enter') &&
          !event.isComposing &&
          !event.shiftKey &&
          !event.altKey &&
          !event.ctrlKey &&
          !event.metaKey
        ) {
          // Accepting only types the suggestion in: the user sends it, edited or not.
          event.preventDefault();
          view.dispatch(view.state.tr.insertText(pending));
          return true;
        }
        onKeyDownRef.current(event);
        return event.defaultPrevented;
      },
    },
    onUpdate({ editor: currentEditor }) {
      const next = serializeContent(currentEditor.getJSON() as TiptapContent);
      lastEmittedValueRef.current = next;
      if (submittedRef.current !== null) submittedRef.current.value = next;
      onChangeRef.current(next);
    },
  });

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (editor === null) return;
    const valueChanged = value !== lastValuePropRef.current;
    lastValuePropRef.current = value;
    // The owner may publish typed text a little later than it was typed: when only the
    // references change, the text is the editor's own, not a value that lags behind it.
    const content = valueChanged ? value : lastEmittedValueRef.current;
    if (content === lastEmittedValueRef.current && referencesKey === lastReferencesKeyRef.current)
      return;
    editor.commands.setContent(
      parseInlineReferenceValue(content, references, variant !== 'default'),
      {
        emitUpdate: false,
      },
    );
    lastEmittedValueRef.current = content;
    lastReferencesKeyRef.current = referencesKey;
    if (submittedRef.current !== null) submittedRef.current.value = content;
  }, [editor, references, referencesKey, value, variant]);

  useImperativeHandle(ref, () => ({
    focus: () => editor?.commands.focus(),
    insertReference(reference, queryLength) {
      if (editor === null) return;
      const { from } = editor.state.selection;
      const queryStart = Math.max(1, from - queryLength - 1);
      editor
        .chain()
        .focus(undefined, { scrollIntoView: false })
        .deleteRange({ from: queryStart, to: from })
        .insertContent([
          { type: 'reference', attrs: reference },
          { type: 'text', text: ' ' },
        ])
        .run();
    },
    insertReferenceAtSelection(reference) {
      editor
        ?.chain()
        .focus(undefined, { scrollIntoView: false })
        .insertContent([
          { type: 'reference', attrs: reference },
          { type: 'text', text: ' ' },
        ])
        .run();
    },
    replaceReference(position, reference) {
      if (editor === null) return;
      const node = editor.state.doc.nodeAt(position);
      if (node?.type.name !== 'reference') return;
      const replacement = editor.schema.nodes['reference']?.create(reference);
      if (!replacement) return;
      editor.view.dispatch(
        editor.state.tr.replaceWith(position, position + node.nodeSize, replacement),
      );
    },
    setSelectionRange(start, end) {
      if (editor === null) return;
      const maximum = editor.state.doc.content.size;
      editor.commands.setTextSelection({
        from: Math.min(start + 1, maximum),
        to: Math.min(end + 1, maximum),
      });
    },
  }));

  return (
    <>
      <div className="relative w-full min-w-0 cursor-text">
        {showSuggestion ? (
          // In the flow, so a long suggestion grows the editor; the empty editor lies over it.
          <div
            aria-hidden
            className="pointer-events-none select-none whitespace-pre-wrap break-words px-4 pt-3.5 pb-2 text-left text-muted-foreground text-sm leading-6"
          >
            {suggestion}
            <kbd className="type-caption ml-1.5 rounded bg-muted px-1.5 py-0.5 font-sans text-muted-foreground pointer-coarse:hidden">
              Tab
            </kbd>
          </div>
        ) : value.length === 0 && (
          <span
            aria-hidden
            className={`pointer-events-none absolute text-left text-muted-foreground leading-6 ${
              variant === 'template'
                ? 'top-0 left-0 text-sm'
                : variant === 'template-field'
                  ? 'top-1.5 right-10 left-2.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs'
                  : 'top-3.5 left-4 text-sm'
            }`}
          >
            {placeholder}
          </span>
        )}
        <div className={showSuggestion ? 'absolute inset-0' : undefined}>
          <EditorContent editor={editor} />
        </div>
      </div>
      <input defaultValue={value} name="message" readOnly ref={submittedRef} type="hidden" />
      <style>{`
        .crowdex-reference {
          display: inline-flex;
          max-width: min(22rem, 70vw);
          height: 1.5rem;
          align-items: baseline;
          flex-wrap: nowrap;
          gap: 0.25rem;
          margin: 0 0.125rem;
          overflow: hidden;
          color: var(--reference);
          vertical-align: baseline;
          white-space: nowrap;
        }
        .crowdex-reference > span:last-child {
          display: block;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .crowdex-reference-leading {
          position: relative;
          display: inline-flex;
          width: 1rem;
          height: 1rem;
          flex: none;
          align-items: center;
          justify-content: center;
        }
        .crowdex-reference-icon,
        .crowdex-reference-remove {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .crowdex-reference-icon svg {
          width: 0.9rem;
          height: 0.9rem;
          stroke-width: 1.8;
          stroke-linecap: round;
          stroke-linejoin: round;
        }
        .crowdex-reference-remove {
          flex: none;
          opacity: 0;
          color: color-mix(in oklab, var(--reference) 75%, transparent);
          cursor: pointer;
        }
        .crowdex-reference:hover .crowdex-reference-icon {
          opacity: 0;
        }
        .crowdex-reference:hover .crowdex-reference-remove {
          opacity: 1;
        }
        .crowdex-reference.ProseMirror-selectednode {
          border-radius: 0.25rem;
          outline: 1px solid color-mix(in oklab, var(--reference) 70%, transparent);
          outline-offset: 2px;
        }
        .crowdex-reference-template {
          height: 1.625rem;
          align-items: center;
          border: 1px solid color-mix(in oklab, var(--reference) 24%, transparent);
          border-radius: 0.4rem;
          background: color-mix(in oklab, var(--reference) 10%, transparent);
          padding: 0 0.45rem 0 0.3rem;
          font-size: 0.75rem;
          line-height: 1;
          cursor: pointer;
        }
      `}</style>
    </>
  );
});
